import React from 'react';
import { FeedbackData, SimulationSettings, CustomerProfile } from '../types';
import { downloadFeedbackFiles } from '../utils/downloadUtils';
import { Download, RefreshCw, CheckCircle2, AlertCircle, MessageSquareText, Timer } from 'lucide-react';
import { getFeedbackUiLabels, getLanguageConfig } from '../utils/languages';

interface FeedbackScreenProps {
    feedbackData: FeedbackData;
    settings: SimulationSettings;
    profile: CustomerProfile;
    timingDetails: string;
    onRestart: () => void;
}

export const FeedbackScreen: React.FC<FeedbackScreenProps> = ({
    feedbackData,
    settings,
    profile,
    timingDetails,
    onRestart
}) => {
    const handleDownloadFeedback = () => {
        downloadFeedbackFiles(feedbackData, settings, profile, timingDetails);
    };

    const t = getFeedbackUiLabels(settings.language);
    const langConfig = getLanguageConfig(settings.language);

    return (
        <div className="feedback-container" lang={langConfig.bcp47} dir={langConfig.dir}>
            <div className="feedback-overall">
                <div className="overall-score-large">
                    <span className="score-num">{feedbackData.overall_score}</span>
                    <span className="score-label">{t.overall}</span>
                </div>
                <div>
                    <h2 style={{ marginBottom: '0.5rem' }}>{t.simulationResults}</h2>
                    <p className="summary-text">{feedbackData.overall_summary}</p>
                    {settings.assessmentRubricTitle && (
                        <p style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{ display: 'inline-block', background: 'rgba(99,102,241,0.1)', color: '#4F46E5', borderRadius: '999px', padding: '0.15rem 0.6rem', fontWeight: 600, fontSize: '0.75rem' }}>
                                📋 {settings.assessmentRubricTitle}
                            </span>
                            {t.assessmentFrameworkApplied}
                        </p>
                    )}
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                        <button className="button button-primary" onClick={handleDownloadFeedback}>
                            <Download size={18} />
                            {t.downloadReport}
                        </button>
                        <button className="button button-secondary" onClick={onRestart}>
                            <RefreshCw size={18} />
                            {t.newTraining}
                        </button>
                    </div>
                </div>
            </div>

            <div className="dashboard-grid">
                <div className="stat-tile">
                    <span className="stat-label">{t.scenario}</span>
                    <span style={{ fontWeight: 600 }}>{settings.scenarioType}</span>
                </div>
                <div className="stat-tile">
                    <span className="stat-label">{t.channel}</span>
                    <span style={{ fontWeight: 600 }}>{settings.channel}</span>
                </div>
                <div className="stat-tile">
                    <span className="stat-label">{t.duration}</span>
                    <span style={{ fontWeight: 600 }}>{timingDetails}</span>
                </div>
                <div className="stat-tile">
                    <span className="stat-label">{t.customer}</span>
                    <span style={{ fontWeight: 600 }}>{feedbackData.customer_name_detected || profile.name}</span>
                </div>
            </div>

            <div className="category-results-grid">
                {feedbackData.feedback && Object.entries(feedbackData.feedback).map(([key, detail]) => {
                    const categoryTitle = feedbackData.category_labels?.[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    if (detail.not_applicable) return null;

                    return (
                        <div key={key} className="category-fb-card">
                            <div className="category-fb-header">
                                <h4>{categoryTitle}</h4>
                                <span className={`score-pill ${detail.score >= 8 ? 'pill-high' : detail.score >= 6 ? 'pill-mid' : 'pill-low'}`}>
                                    {detail.score} / {detail.max_score}
                                </span>
                            </div>

                            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                                {detail.explanation_for_deduction}
                            </p>

                            <div className="fb-points-box">
                                {detail.positive?.map((p, i) => (
                                    <div key={i} className="fb-point fb-pos">
                                        <CheckCircle2 size={16} />
                                        <span>{p}</span>
                                    </div>
                                ))}
                                {detail.improvement?.map((p, i) => (
                                    <div key={i} className="fb-point fb-imp">
                                        <AlertCircle size={16} />
                                        <span>{p}</span>
                                    </div>
                                ))}
                                {detail.recommended_scripts?.map((p, i) => (
                                    <div key={i} className="fb-point fb-script">
                                        <MessageSquareText size={16} />
                                        <span>{p}</span>
                                    </div>
                                ))}
                                {detail.estimated_time_spent_seconds && (
                                    <div className="fb-point" style={{ opacity: 0.7 }}>
                                        <Timer size={16} />
                                        <span>{t.focusedFor(Math.round(detail.estimated_time_spent_seconds))}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {feedbackData.timing_analysis_summary && (
                <div className="card" style={{ marginTop: '2.5rem', background: '#F8FAFC' }}>
                    <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>{t.timelineAnalysis}</h3>
                    <p style={{ whiteSpace: 'pre-wrap', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                        {feedbackData.timing_analysis_summary}
                    </p>
                </div>
            )}
        </div>
    );
};
